import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useMediaQuery } from '@mui/material';

/**
 * Hook to detect if device is mobile/tablet
 */
export function useIsMobile() {
  return useMediaQuery('(max-width: 768px)');
}

/**
 * Hook to detect if device is iPad specifically
 */
export function useIsIPad() {
  return useMediaQuery('(min-width: 768px) and (max-width: 1180px)');
}

/**
 * Hook to detect 12.9" iPad Pro specifically (both orientations)
 */
export function useIsIPadPro129() {
  return useMediaQuery(
    '((min-width: 1024px) and (max-width: 1366px) and (min-height: 1024px) and (max-height: 1366px))'
  );
}

/**
 * Hook for GPU acceleration styles
 */
export function useGPUAcceleration() {
  return useMemo(() => ({
    transform: 'translateZ(0)',
    willChange: 'transform',
    contain: 'layout style paint',
  }), []);
}

/**
 * Hook for mobile-optimized styles
 * Removes expensive CSS on mobile devices
 */
export function useMobileOptimizedStyles() {
  const isMobile = useIsMobile();

  return useMemo(() => ({
    // Remove shadows on mobile
    boxShadow: isMobile ? 'none' : '0 4px 20px rgba(0,0,0,0.15)',

    // Simplify borders
    borderRadius: isMobile ? 1 : 2,

    // Disable expensive filters
    backdropFilter: isMobile ? 'none' : 'blur(10px)',

    // Faster transitions
    transition: isMobile ? 'all 150ms ease' : 'all 250ms ease',

    // GPU acceleration
    ...(isMobile ? {
      transform: 'translateZ(0)',
      willChange: 'transform',
    } : {}),
  }), [isMobile]);
}

/**
 * Hook for touch-optimized styles
 * Improves touch interaction on mobile
 */
export function useTouchOptimizedStyles() {
  const isMobile = useIsMobile();

  return useMemo(() => ({
    // Remove tap delay
    touchAction: 'manipulation',

    // Better tap highlight
    WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',

    // Minimum touch target size
    minHeight: isMobile ? 44 : 36,
    minWidth: isMobile ? 44 : 'auto',

    // Prevent text selection
    WebkitUserSelect: 'none',
    userSelect: 'none',

    // Smooth scrolling
    WebkitOverflowScrolling: 'touch',
  }), [isMobile]);
}

/**
 * Hook for passive scroll listener
 * Improves scroll performance by not blocking the main thread
 */
export function usePassiveScroll(
  callback: (event: Event) => void,
  elementId?: string
) {
  const callbackRef = useRef(callback);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const element = elementId
      ? document.getElementById(elementId)
      : window;

    if (!element) return;

    const handler = (e: Event) => {
      callbackRef.current(e);
    };

    // Passive listeners don't block scrolling
    element.addEventListener('scroll', handler, { passive: true } as AddEventListenerOptions);

    return () => {
      element.removeEventListener('scroll', handler);
    };
  }, [elementId]);
}

/**
 * Hook for throttled function calls
 * Limits how often a function can be called
 */
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 100
): T {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastRun = useRef(Date.now());

  return useCallback((...args: Parameters<T>) => {
    const now = Date.now();

    if (now - lastRun.current >= delay) {
      callback(...args);
      lastRun.current = now;
    } else {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        callback(...args);
        lastRun.current = Date.now();
      }, delay);
    }
  }, [callback, delay]) as T;
}

/**
 * Hook for touch event optimization
 * Adds passive touch listeners to improve scroll performance
 */
export function useTouchEvents(
  onTouchStart?: (e: TouchEvent) => void,
  onTouchMove?: (e: TouchEvent) => void,
  onTouchEnd?: (e: TouchEvent) => void,
  elementId?: string
) {
  const touchStartRef = useRef(onTouchStart);
  const touchMoveRef = useRef(onTouchMove);
  const touchEndRef = useRef(onTouchEnd);

  useEffect(() => {
    touchStartRef.current = onTouchStart;
    touchMoveRef.current = onTouchMove;
    touchEndRef.current = onTouchEnd;
  }, [onTouchStart, onTouchMove, onTouchEnd]);

  useEffect(() => {
    const element = elementId
      ? document.getElementById(elementId)
      : window;

    if (!element) return;

    const handleTouchStart = (e: Event) => {
      touchStartRef.current?.(e as TouchEvent);
    };

    const handleTouchMove = (e: Event) => {
      touchMoveRef.current?.(e as TouchEvent);
    };

    const handleTouchEnd = (e: Event) => {
      touchEndRef.current?.(e as TouchEvent);
    };

    const options: AddEventListenerOptions = { passive: true };

    element.addEventListener('touchstart', handleTouchStart, options);
    element.addEventListener('touchmove', handleTouchMove, options);
    element.addEventListener('touchend', handleTouchEnd, options);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [elementId]);
}

/**
 * Hook to detect reduced motion preference
 * Respects user's accessibility settings
 */
export function usePrefersReducedMotion() {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/**
 * Hook for animation styles that respect reduced motion
 */
export function useAccessibleAnimation(duration: string = '0.3s') {
  const prefersReducedMotion = usePrefersReducedMotion();

  return useMemo(() => ({
    transition: prefersReducedMotion ? 'none' : `all ${duration} ease`,
    animation: prefersReducedMotion ? 'none' : undefined,
  }), [prefersReducedMotion, duration]);
}

/**
 * Hook for responsive font sizes
 */
export function useResponsiveFontSize(desktopSize: number) {
  const isMobile = useIsMobile();

  return useMemo(() => {
    return isMobile ? desktopSize * 0.875 : desktopSize;
  }, [isMobile, desktopSize]);
}

/**
 * Hook to get optimal styles for scrollable containers
 */
export function useScrollableContainerStyles() {
  const isMobile = useIsMobile();

  return useMemo(() => ({
    // GPU acceleration
    transform: 'translateZ(0)',
    willChange: 'transform',

    // Containment
    contain: 'layout style paint',

    // Smooth scrolling on iOS
    WebkitOverflowScrolling: 'touch',

    // Remove overscroll behavior on mobile
    overscrollBehavior: isMobile ? 'none' : 'auto',

    // Hide scrollbar on mobile for cleaner look
    '&::-webkit-scrollbar': isMobile ? {
      display: 'none',
    } : {},
    scrollbarWidth: isMobile ? 'none' : 'auto',
  }), [isMobile]);
}

/**
 * Hook for touch-optimized click handlers
 * Handles both click and touch events for better mobile responsiveness
 * Eliminates the 300ms tap delay on iOS
 */
export function useTouchClick<T extends HTMLElement = HTMLElement>(
  onClick: (e: React.MouseEvent<T> | React.TouchEvent<T>) => void
) {
  const handleInteraction = useCallback((e: React.MouseEvent<T> | React.TouchEvent<T>) => {
    // Prevent default to avoid double-firing on some devices
    if (e.type === 'touchend') {
      e.preventDefault();
    }
    onClick(e);
  }, [onClick]);

  return useMemo(() => ({
    onClick: handleInteraction,
    onTouchEnd: handleInteraction,
    sx: {
      touchAction: 'manipulation',
      cursor: 'pointer',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
    },
  }), [handleInteraction]);
}
